import { useState, useRef, useEffect, useCallback } from 'react';
import {
  createBatch as createBatchApi,
  fetchBatch,
  cancelBatch as cancelBatchApi,
} from '../services/generatorBatchApi';
import { isTerminalStatus } from '../utils/constants';

const POLL_INTERVAL_MS = 2000;

/**
 * Avvia un batch e ne fa polling fino allo stato terminale.
 *
 * Garanzie richieste dal contratto/architettura:
 * - nessun timer resta attivo dopo l'unmount;
 * - le richieste obsolete vengono annullate (AbortController);
 * - non si assume che tutti i task abbiano successo (lo stato guida la UI).
 */
export function useBatchRunner() {
  const [batch, setBatch] = useState(null); // dettaglio completo (tasks inclusi)
  const [starting, setStarting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);
  // Flag dedicato per il caso 422 NO_OUTDATED_TASKS (nulla da rigenerare).
  const [noOutdatedTasks, setNoOutdatedTasks] = useState(false);

  const mountedRef = useRef(true);
  const timerRef = useRef(null);
  const abortRef = useRef(null);
  const batchIdRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const abortInFlight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  // Cleanup globale allo smontaggio: nessun timer, nessuna richiesta pendente.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
      abortInFlight();
    };
  }, [clearTimer, abortInFlight]);

  const poll = useCallback(
    (batchId) => {
      if (!mountedRef.current) return;
      abortInFlight();
      const controller = new AbortController();
      abortRef.current = controller;

      fetchBatch(batchId, controller.signal)
        .then((detail) => {
          if (!mountedRef.current || batchIdRef.current !== batchId) return;
          setBatch(detail);
          if (isTerminalStatus(detail?.status)) {
            setPolling(false);
            clearTimer();
          } else {
            clearTimer();
            timerRef.current = setTimeout(() => poll(batchId), POLL_INTERVAL_MS);
          }
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
          if (!mountedRef.current) return;
          setError('Errore durante il polling dello stato del batch.');
          // Riprova comunque: uno stato instabile non deve fermare il monitoraggio.
          clearTimer();
          timerRef.current = setTimeout(() => poll(batchId), POLL_INTERVAL_MS);
        });
    },
    [abortInFlight, clearTimer],
  );

  const start = useCallback(
    (body) => {
      setStarting(true);
      setError(null);
      setNoOutdatedTasks(false);
      abortInFlight();
      const controller = new AbortController();
      abortRef.current = controller;

      return createBatchApi(body, controller.signal)
        .then((created) => {
          if (!mountedRef.current) return null;
          batchIdRef.current = created.batchId;
          setBatch(created);
          setPolling(true);
          // Avvia il polling del dettaglio.
          poll(created.batchId);
          return created;
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return null;
          const resp = err?.response?.data;
          const details = Array.isArray(resp?.details) ? resp.details : [];
          const isNoOutdated =
            resp?.error === 'NO_OUTDATED_TASKS' ||
            details.some((d) => d?.code === 'NO_OUTDATED_TASKS');
          if (isNoOutdated) {
            setNoOutdatedTasks(true);
            setError(
              'Tutte le combinazioni risultano già aggiornate. Puoi forzare una nuova generazione dalla modalità del riepilogo.',
            );
          } else {
            setError(resp?.message || 'Impossibile avviare il batch.');
          }
          return null;
        })
        .finally(() => {
          if (mountedRef.current) setStarting(false);
        });
    },
    [abortInFlight, poll],
  );

  /**
   * Carica un batch esistente (es. dallo storico) e ne riprende il polling se
   * ancora attivo. Riusa lo stesso meccanismo di cleanup.
   */
  const loadExisting = useCallback(
    (batchId) => {
      if (!batchId) return;
      clearTimer();
      batchIdRef.current = batchId;
      setError(null);
      setPolling(true);
      poll(batchId);
    },
    [clearTimer, poll],
  );

  const cancel = useCallback(() => {
    const batchId = batchIdRef.current;
    if (!batchId) return Promise.resolve();
    setCancelling(true);
    return cancelBatchApi(batchId)
      .then(() => {
        if (mountedRef.current && !isTerminalStatus(batchIdRef.current && batch?.status)) {
          // Il polling rifletterà il passaggio a CANCELLED.
          poll(batchId);
        }
      })
      .catch(() => {
        if (mountedRef.current) setError('Impossibile annullare il batch.');
      })
      .finally(() => {
        if (mountedRef.current) setCancelling(false);
      });
  }, [batch, poll]);

  const reset = useCallback(() => {
    clearTimer();
    abortInFlight();
    batchIdRef.current = null;
    setBatch(null);
    setPolling(false);
    setStarting(false);
    setCancelling(false);
    setError(null);
    setNoOutdatedTasks(false);
  }, [clearTimer, abortInFlight]);

  const clearNoOutdated = useCallback(() => setNoOutdatedTasks(false), []);

  return {
    batch,
    starting,
    polling,
    cancelling,
    error,
    noOutdatedTasks,
    start,
    cancel,
    reset,
    loadExisting,
    clearNoOutdated,
  };
}
